import { Component, OnInit, ViewChild } from '@angular/core';
import { DialogVariant } from '@shared/confirm-dialog/confirm-dialog.component';
import { DeleteRoomComponent } from './delete-room/delete-room.component';
import {
  ClinicResponse,
  ClinicRoom,
  ClinicPayload,
  Clinic,
  ClinicLetterheadPayload,
  StoredImageDetail,
  PrescriptionPdfAssetsPayload,
  PrescriptionPdfAssets,
} from './managed-clinic.model';
import { AddRoomComponent } from './add-room/add-room.component';
import { MatTableDataSource } from '@angular/material/table';
import { MatSort } from '@angular/material/sort';
import { MatPaginator } from '@angular/material/paginator';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Details } from '@features/masters/masters.model';
import { MatDialog } from '@angular/material/dialog';
import { ManagedClinicService } from './managed-clinic.service';
import { CommonService } from '@app/services/common.service';
import { AddClinicComponent } from './add-clinic/add-clinic.component';
import { ActivatedRoute, Router } from '@angular/router';
import { ChatMediaPreviewComponent } from '@features/process-scheduler/chat/chat-media-preview/chat-media-preview.component';

@Component({
  selector: 'app-managed-clinic',
  templateUrl: './managed-clinic.component.html',
  styleUrls: ['./managed-clinic.component.scss'],
})
export class ManagedClinicComponent implements OnInit {
  @ViewChild(MatSort, { static: false }) set matSort(ms: MatSort) {
    if (ms) {
      this.dataSource.sort = ms;
      ms.sortChange.subscribe((sort) => {
        this.sortColumn = sort.active;
        this.sortDirection = sort.direction || 'asc';
        this.updateQueryParams();
      });
    }
  }
  @ViewChild(MatPaginator, { static: false }) set matPaginator(
    mp: MatPaginator
  ) {
    if (mp) {
      this.dataSource.paginator = mp;
    }
  }
  letterheadForm: FormGroup;
  selectedData: Details;
  clinicsList: ClinicResponse;
  isLetterheadDrawerOpen: boolean = false;
  selectedClinicForLetterhead: ClinicPayload | null = null;
  letterheadImagePreview: LetterheadPreviewState = {
    headerImage: '',
    footerImage: '',
    watermarkImage: '',
  };
  imageLoadingState: LetterheadLoadingState = {
    headerImage: false,
    footerImage: false,
    watermarkImage: false,
  };
  isLetterheadLoading: boolean = false;
  isSavingLetterhead: boolean = false;

  searchValue: string = '';
  sortColumn: string = 'createdAt';
  sortDirection: string = 'desc';

  constructor(
    private managedClinicServices: ManagedClinicService,
    private commonService: CommonService,
    private dialog: MatDialog,
    private formBuilder: FormBuilder,
    private router: Router,
    private route: ActivatedRoute
  ) {
    this.letterheadForm = this.formBuilder.group({
      letterheadName: ['', [Validators.maxLength(85)]],
      headerImage: [null, Validators.required],
      footerImage: [null, Validators.required],
      watermarkImage: [null],
    });
    this.dataSource.filterPredicate = (data: any, filter) => {
      const dataStr = JSON.stringify(data).toLowerCase();
      return dataStr.indexOf(filter) != -1;
    };
    this.dataSource.sortingDataAccessor = (item, path) =>
      commonService.getNestedObjectValue(item, path) || '';
  }

  ngOnInit(): void {
    this.loadQueryParams();
    this.loadData();
  }

  loadQueryParams() {
    this.route.queryParams.subscribe((params) => {
      if (params['search']) {
        this.searchValue = params['search'];
        this.dataSource.filter = this.searchValue.trim().toLowerCase();
      } else {
        this.searchValue = '';
        this.dataSource.filter = '';
      }

      if (params['sortColumn']) {
        this.sortColumn = params['sortColumn'];
      } else {
        this.sortColumn = 'createdAt';
      }

      if (params['sortDirection']) {
        this.sortDirection = params['sortDirection'];
      } else {
        this.sortDirection = 'desc';
      }

      if (this.dataSource.sort) {
        this.dataSource.sort.active = this.sortColumn;
        this.dataSource.sort.direction = this.sortDirection as 'asc' | 'desc';
        this.dataSource.sort.sortChange.emit({
          active: this.sortColumn,
          direction: this.sortDirection as 'asc' | 'desc',
        });
      }
    });
  }

  updateQueryParams() {
    const queryParams: any = {};
    if (this.searchValue) {
      queryParams.search = this.searchValue;
    }
    if (this.sortColumn !== 'createdAt') {
      queryParams.sortColumn = this.sortColumn;
    }
    if (this.sortDirection !== 'desc') {
      queryParams.sortDirection = this.sortDirection;
    }
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: queryParams,
    });
  }

  private loadData() {
    this.managedClinicServices.getClinicList().subscribe((response) => {
      this.dataSource.data = response.clinics;
      if (this.searchValue) {
        this.dataSource.filter = this.searchValue.trim().toLowerCase();
      }
    });
  }

  onFilterValueChange(filterValue: string) {
    this.searchValue = filterValue;
    this.dataSource.filter = filterValue.trim().toLowerCase();
    this.updateQueryParams();
  }

  add() {
    const dialogRef = this.dialog.open(AddClinicComponent);

    dialogRef.afterClosed().subscribe((isSuccess) => {
      if (isSuccess) this.loadData();
    });
  }

  displayedColumns: string[] = [
    'details.name',
    'details.address',
    'createdAt',
    'rooms',
    'actions',
  ];

  dataSource: MatTableDataSource<Clinic> = new MatTableDataSource<Clinic>([]);
  createRoomHtml(rooms: Array<ClinicRoom>): {
    text: string;
    count: number;
    all: string;
  } {
    if (!rooms) {
      return { text: '', count: 0, all: '' };
    }
    let text = '';
    let i = 0;
    text = rooms[i].name;
    i++;
    if (text.length < 10 && rooms.length > 1) {
      text = text + ', ' + rooms[i].name;
      i++;
    }
    return {
      text: text,
      count: rooms.length - i,
      all: rooms.map((room) => room.name).join(','),
    };
  }

  addClinicRoom(element: ClinicPayload) {
    const digRef = this.dialog.open(AddRoomComponent, {
      data: {
        clinic_id: element.id,
        list: element?.rooms ? element?.rooms : [],
      },
      width: '50vw',
      minWidth: '300px',
    });
    digRef.afterClosed().subscribe((res) => {
      if (res) {
        this.loadData();
      }
    });
  }
  deleteClinicRoom(element: ClinicPayload) {
    const digRef = this.dialog.open(DeleteRoomComponent, {
      data: {
        clinic_id: element.id,
        list: element?.rooms ? element?.rooms : [],
      },
      width: '50vw',
      minWidth: '300px',
    });
    digRef.afterClosed().subscribe((res) => {
      if (res) {
        this.commonService.confirmDialogOk({
          dialog: this.dialog,
          message: LocalTranslations.DeleteSuccess,
          title: 'Success',
          dialogVariant: DialogVariant.OK,
        });
        this.loadData();
      }
    });
  }
  editClinic(details: Clinic, id: string) {
    const dialogRef = this.dialog.open(AddClinicComponent, {
      data: { ...details, id },
      disableClose: true,
    });
    dialogRef.afterClosed().subscribe((isSucces) => {
      if (isSucces) {
        this.loadData();
      }
    });
  }
  openAddServiceDialog(id: string) {
    // const dialogRef = this.dialog.open(MapServiceDialogComponent, {
    //   data: { id },
    //   width: '450px',
    // });
    this.router.navigate(['managed-clinic/map-service'], {
      queryParams: { id: id },
    });
  }

  openSubStore(id: string) {
    this.router.navigate(['managed-clinic/sub-store'], {
      queryParams: { id: id },
    });
  }

  openLetterheadDrawer(clinic: ClinicPayload) {
    this.selectedClinicForLetterhead = clinic;
    this.isLetterheadDrawerOpen = true;
    this.resetLetterheadForm();
    if (clinic?.id) {
      this.loadClinicPrescriptionPdfAssets(clinic.id);
    }
  }

  closeLetterheadDrawer() {
    this.isLetterheadDrawerOpen = false;
  }

  onLetterheadImageChange(event: Event, field: LetterheadImageField) {
    const inputElement = event.target as HTMLInputElement;
    const file = inputElement.files?.[0];
    if (!file) {
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const dataURI = (reader.result as string) || '';
      this.letterheadImagePreview[field] = dataURI;
      this.imageLoadingState[field] = false;
      this.letterheadForm.get(field)?.setValue({
        name: file.name,
        dataURI: dataURI,
      });
    };
    reader.readAsDataURL(file);
    inputElement.value = '';
  }

  clearLetterheadImage(field: LetterheadImageField) {
    this.letterheadForm.get(field)?.setValue(null);
    this.letterheadImagePreview[field] = '';
    this.imageLoadingState[field] = false;
  }

  onLetterheadPreviewLoad(field: LetterheadImageField) {
    this.imageLoadingState[field] = false;
  }

  onLetterheadPreviewError(field: LetterheadImageField) {
    this.imageLoadingState[field] = false;
  }

  getLetterheadImageDisplayName(field: LetterheadImageField): string {
    const value = this.letterheadForm.get(field)?.value as
      | UploadedImage
      | StoredImageDetail
      | null;
    if (!value) {
      return '';
    }
    const fileName =
      'originalFileName' in value ? value.originalFileName : undefined;
    return fileName || value.name || '';
  }

  openLetterheadImagePreview(selectedField: LetterheadImageField) {
    const fields: LetterheadImageField[] = [
      'headerImage',
      'footerImage',
      'watermarkImage',
    ];

    const files = fields
      .map((field) => {
        const fileValue = this.letterheadForm.get(field)?.value;
        const previewUrl =
          fileValue?.dataURI || this.letterheadImagePreview[field];

        if (!previewUrl) {
          return null;
        }

        return {
          field,
          previewUrl,
          type: 'image',
          fileType: 'image',
          mimeType: 'image/*',
          name:
            fileValue?.originalFileName ||
            fileValue?.name ||
            this.getLetterheadImageName(field),
        };
      })
      .filter((file): file is NonNullable<typeof file> => !!file);

    if (!files.length) {
      return;
    }

    const startIndex = files.findIndex((file) => file.field === selectedField);

    this.dialog.open(ChatMediaPreviewComponent, {
      data: {
        files,
        startIndex: startIndex >= 0 ? startIndex : 0,
      },
      panelClass: 'full-screen-modal',
      maxWidth: '100%',
      maxHeight: '100%',
    });
  }

  private getLetterheadImageName(field: LetterheadImageField): string {
    if (field === 'headerImage') {
      return 'Logo Image';
    }
    if (field === 'footerImage') {
      return 'Footer Image';
    }
    return 'Side Banner';
  }

  saveLetterhead() {
    const clinicId = this.selectedClinicForLetterhead?.id;
    if (!clinicId) {
      this.commonService.confirmDialogOk({
        dialog: this.dialog,
        title: 'Error',
        message: LocalTranslations.ClinicMissing,
        dialogVariant: DialogVariant.OK,
      });
      return;
    }

    if (this.letterheadForm.invalid || this.isSavingLetterhead) {
      this.letterheadForm.markAllAsTouched();
      return;
    }

    const payload: PrescriptionPdfAssetsPayload = {
      footerText: (this.letterheadForm.get('letterheadName')?.value || '')
        .toString()
        .trim(),
    };

    const logo = this.getUploadedImageValue('headerImage');
    const sideImage = this.getUploadedImageValue('watermarkImage');
    const bottomImage = this.getUploadedImageValue('footerImage');

    if (logo) {
      payload.logo = logo;
    }
    if (sideImage) {
      payload.sideImage = sideImage;
    }
    if (bottomImage) {
      payload.bottomImage = bottomImage;
    }

    this.isSavingLetterhead = true;
    this.managedClinicServices
      .savePrescriptionPdfAssets(clinicId, payload)
      .subscribe({
        next: () => {
          this.isSavingLetterhead = false;
          this.commonService.confirmDialogOk({
            dialog: this.dialog,
            title: 'Success',
            message: LocalTranslations.LetterheadSaved,
            dialogVariant: DialogVariant.OK,
          });
          this.closeLetterheadDrawer();
          this.resetLetterheadForm();
        },
        error: () => {
          this.isSavingLetterhead = false;
          this.commonService.confirmDialogOk({
            dialog: this.dialog,
            title: 'Error',
            message: LocalTranslations.LetterheadSaveFailed,
            dialogVariant: DialogVariant.OK,
          });
        },
      });
  }

  private loadClinicPrescriptionPdfAssets(clinicId: string) {
    this.isLetterheadLoading = true;
    this.managedClinicServices.getClinicDetail(clinicId).subscribe({
      next: (response) => {
        this.patchPrescriptionPdfAssets(
          response?.clinic?.prescriptionPdfAssets
        );
        this.isLetterheadLoading = false;
      },
      error: () => {
        this.isLetterheadLoading = false;
        this.commonService.confirmDialogOk({
          dialog: this.dialog,
          title: 'Error',
          message: LocalTranslations.ClinicDetailFetchFailed,
          dialogVariant: DialogVariant.OK,
        });
      },
    });
  }

  private patchPrescriptionPdfAssets(assets?: PrescriptionPdfAssets) {
    if (!assets) {
      return;
    }

    const logo = this.getStoredImageAsset(assets.logo);
    const bottomImage = this.getStoredImageAsset(assets.bottomImage);
    const sideImage = this.getStoredImageAsset(assets.sideImage);

    this.letterheadForm.patchValue({
      letterheadName: assets.footerText || '',
      headerImage: logo,
      footerImage: bottomImage,
      watermarkImage: sideImage,
    });

    this.letterheadImagePreview = {
      headerImage: assets.logoUrl || '',
      footerImage: assets.bottomImageUrl || '',
      watermarkImage: assets.sideImageUrl || '',
    };
    this.imageLoadingState = {
      headerImage: !!assets.logoUrl,
      footerImage: !!assets.bottomImageUrl,
      watermarkImage: !!assets.sideImageUrl,
    };
  }

  private getStoredImageAsset(asset?: StoredImageDetail | null) {
    if (!asset) {
      return null;
    }
    return {
      id: asset.id,
      name: asset.name,
      mimeType: asset.mimeType,
      originalFileName: asset.originalFileName,
    };
  }

  private getUploadedImageValue(
    field: LetterheadImageField
  ): UploadedImage | undefined {
    const value = this.letterheadForm.get(field)?.value as
      | UploadedImage
      | StoredImageDetail
      | null;

    if (value && 'dataURI' in value && value.dataURI) {
      return { name: value.name, dataURI: value.dataURI };
    }

    return undefined;
  }

  private resetLetterheadForm() {
    this.letterheadForm.reset({
      letterheadName: '',
      headerImage: null,
      footerImage: null,
      watermarkImage: null,
    });
    this.letterheadImagePreview = {
      headerImage: '',
      footerImage: '',
      watermarkImage: '',
    };
    this.imageLoadingState = {
      headerImage: false,
      footerImage: false,
      watermarkImage: false,
    };
    this.isLetterheadLoading = false;
    this.isSavingLetterhead = false;
  }
}

enum LocalTranslations {
  DeleteSuccess = 'Room(s) Deleted Succesfully',
  LetterheadSaved = 'Prescription saved successfully',
  LetterheadSaveFailed = 'Failed to save Prescription ',
  ClinicMissing = 'Clinic not found for letterhead mapping',
  ClinicDetailFetchFailed = 'Failed to fetch clinic details',
}

type LetterheadImageField = 'headerImage' | 'footerImage' | 'watermarkImage';
type UploadedImage = { name: string; dataURI: string };

type LetterheadPreviewState = Record<LetterheadImageField, string>;
type LetterheadLoadingState = Record<LetterheadImageField, boolean>;
